import React, { ReactNode, useState } from 'react'
import Link from 'next/link'
import { useFindMenu, useTrans } from '@next-nx/hooks'
import { Breadcrumbs, MainTitle, TabGroup, WContainer } from '@next-nx/shared-ui/atoms'
import { CompareBar, HelpBanner, Page, ProdottiListing } from '@next-nx/shared-ui/organisms'
import { ArticoloCard } from '@next-nx/shared-ui/molecules'
import styles from './SearchTemplate.module.sass'
import {
  MODELLO_SEARCH_ARTICOLI,
  MODELLO_SEARCH_CATEGORIE,
  MODELLO_SEARCH_FAQ,
  MODELLO_SEARCH_OFFERTA,
  MODELLO_SEARCH_PAGINE,
} from '@next-nx/utils/search'
import { CategoriaType, ListingType } from '@next-nx/utils/types'

interface Props {
  query?: string
  option?: string
  pagina?: any
  categorie?: any[]
  articoli?: any[]
  pagine?: any[]
  faq?: any[]
  categoria: CategoriaType
  filtro_categoria: string
  listing?: ListingType
}

const SearchTemplate = (props: Props) => {
  const {
    pagina = null,
    query = '',
    option = '',
    categorie = [],
    articoli = [],
    pagine = [],
    faq = [],
    categoria,
    filtro_categoria,
    listing,
  } = props

  const t = useTrans()
  const helpBannerMenu = useFindMenu('help_banner')
  const helpBannerListingMenu = useFindMenu('listing-pdp', helpBannerMenu)

  const [compareBarCollapsed, setCompareBarCollapsed] = useState(false) // Da istruzioni: al primo atterraggio visibile

  const tabs: {
    key: string
    label?: string
    disabled?: boolean
    content: ReactNode
  }[] = [
    {
      key: MODELLO_SEARCH_OFFERTA,
      label: `${t('Prodotti')} (${listing?.number})`,
      content:
        listing && listing?.number ? (
          <div className={`${styles.tabContent} ${styles['tabContent--no-bottom']}`}>
            <ProdottiListing
              search={query}
              categoria={categoria}
              listing={listing}
              filtro_categoria={filtro_categoria}
            />
          </div>
        ) : null,
    },
    {
      key: MODELLO_SEARCH_CATEGORIE,
      label: `${t('Categorie')} (${categorie.length})`,
      content:
        categorie?.length > 0 ? (
          <div className={styles.tabContent}>
            <p className={styles.tabContentTitle}>
              {categorie.length > 1
                ? t('{0} categorie trovate', [categorie.length.toString()])
                : t('1 categoria trovata')}
            </p>
            <div className={styles.tabContentItems}>
              {categorie.map((x, index) => (
                <div key={index}>
                  <Link href={x?.url || ''}>
                    <a>
                      {x.titoloCategoriaPrimaria ? (
                        <>
                          <span className={styles.categoriaTitle}>{x.titolo}</span> {t('in')}{' '}
                          {x.titoloCategoriaPrimaria}
                        </>
                      ) : (
                        x.titolo
                      )}
                    </a>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        ) : null,
    },
    {
      key: MODELLO_SEARCH_ARTICOLI,
      label: `${t('Articoli')} (${articoli.length})`,
      content:
        articoli?.length > 0 ? (
          <div className={styles.tabContent}>
            <p className={styles.tabContentTitle}>
              {articoli.length > 1
                ? t('{0} articoli trovati', [articoli.length.toString()])
                : t('1 articoli trovato')}
            </p>
            <div className={styles.tabContentItems}>
              <div className={styles.newsListing}>
                {articoli.map((x, index) => (
                  <div className={styles.newsItem} key={index}>
                    <ArticoloCard news={x} variant="horizontal" ctaLabel={t('Leggi di più')} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null,
    },
    {
      key: MODELLO_SEARCH_PAGINE,
      label: `${t('Pagine')} (${pagine.length})`,
      content:
        pagine?.length > 0 ? (
          <div className={styles.tabContent}>
            <p className={styles.tabContentTitle}>
              {pagine.length > 1
                ? t('{0} pagine trovate', [pagine.length.toString()])
                : t('1 pagina trovata')}
            </p>
            <div className={styles.tabContentItems}>
              {pagine.map((x, index) => (
                <div key={index}>
                  <Link href={x?.url || ''}>
                    <a>{x.titolo}</a>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        ) : null,
    },
    {
      key: MODELLO_SEARCH_FAQ,
      label: `${t('FAQ')} (${faq.length})`,
      content:
        faq?.length > 0 ? (
          <div className={styles.tabContent}>
            <p className={styles.tabContentTitle}>
              {faq.length > 1 ? t('{0} FAQ trovate', [faq.length.toString()]) : t('1 FAQ trovata')}
            </p>
            <div className={styles.tabContentItems}>
              {faq.map((x, index) => (
                <div key={index}>
                  <Link href={x?.url || ''}>
                    <a>{x.titolo}</a>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        ) : null,
    },
  ].filter((x) => x.content)

  const activeModels = tabs.map((x) => x.key)

  const [activeTab, setActiveTab] = useState(
    activeModels.includes(option) ? option : activeModels?.[0]
  )

  return (
    <Page pagina={pagina} item={{}} contentClassName={styles.searchResultsContent}>
      <WContainer>
        <Breadcrumbs breadcrumbs={pagina.breadcrumbs} />
        {!!pagina.titolo && <MainTitle label={pagina.titolo} className={styles.title} safe />}

        {tabs.length > 0 ? (
          <div className={styles.tabs}>
            <TabGroup
              variant="full"
              tabs={tabs}
              triggersClassName={styles.tabsTriggers}
              initialActiveTab={activeTab}
              setExternalActiveTab={setActiveTab}
            />
          </div>
        ) : (
          <p className={styles.noResults}>{t('Non ci sono risultati per questa ricerca')}</p>
        )}
      </WContainer>
      {helpBannerListingMenu && (
        <HelpBanner item={helpBannerListingMenu} className={styles.helpBanner} />
      )}
      {activeTab === MODELLO_SEARCH_OFFERTA && (
        <CompareBar
          type="plp"
          collapsed={compareBarCollapsed}
          setCollapsed={(v) => setCompareBarCollapsed(v)}
          url={'/comparatore'} // TODO
          className={styles.compareBar}
        />
      )}
    </Page>
  )
}

export default SearchTemplate
